'use client';

import React, { createContext, useContext, useReducer, useRef } from 'react';
import { IAction } from '@framework/state/createAction';
import reducer, { initialState, ITraceFiltersState } from './reducer';
import getActions from './getActions';

const useTraceFilters = () => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const stateRef = useRef<ITraceFiltersState>(state);

  // getState returns the latest state even before React re-renders, as Redux getState did.
  const dispatchAndSync = (action: IAction) => {
    stateRef.current = reducer(stateRef.current, action);
    dispatch(action);
  };

  const actions = getActions({ dispatch: dispatchAndSync, getState: () => stateRef.current });

  return {
    ...state,
    itemsFilter: state.items?.filter || {},
    ...actions,
  };
};

type TraceFiltersContextType = ReturnType<typeof useTraceFilters>;

export const TraceFiltersContext = createContext<TraceFiltersContextType>(null);

export const useTraceFiltersContext = () => {
  const context = useContext(TraceFiltersContext);
  if (!context) {
    throw new Error('useTraceFiltersContext must be used within the TraceFiltersContext');
  }
  return context;
};

interface IProps {
  children: React.ReactNode,
}

const TraceFiltersContextProvider = ({ children }: IProps) => (
  <TraceFiltersContext.Provider value={ useTraceFilters() }>
    { children }
  </TraceFiltersContext.Provider>
);

export default TraceFiltersContextProvider;
